import { useId } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import type { Position } from "../../api/types";
import { employmentTypeLabel } from "./PositionFormModal";
import { VideoEmbed } from "./VideoEmbed";

// PositionDetail is a modal (not a route) opened from a card: the full
// description, every video inline, Respond, and — for managers — edit /
// close / reopen / delete controls delegated back to the list page.
export function PositionDetail({
  position,
  canManage,
  error,
  onClose,
  onRespond,
  onEdit,
  onToggleStatus,
  onDelete
}: {
  position: Position;
  canManage: boolean;
  error: string | null;
  onClose: () => void;
  onRespond: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const titleId = useId();
  const closed = position.status === "closed";
  const overlayDismiss = useOverlayDismiss(onClose);

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div
        className="modal position-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal__header">
          <h2 id={titleId}>{position.title}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="modal__body">
          <div className="position-detail__tags">
            <span className="chip">{position.department_name}</span>
            <span className="chip">
              {position.slots} opening{position.slots === 1 ? "" : "s"}
            </span>
            {position.employment_type !== undefined && (
              <span className="chip">{employmentTypeLabel(position.employment_type)}</span>
            )}
            {position.location !== undefined && <span className="chip">{position.location}</span>}
            {position.compensation_range !== undefined && (
              <span className="chip chip--impact">{position.compensation_range}</span>
            )}
            {closed && <span className="status-badge status-badge--closed">closed</span>}
          </div>
          {canManage && position.contact_email !== undefined && (
            <p className="muted">Responses go to {position.contact_email}.</p>
          )}
          {/* Trusted HTML: the server sanitizes every description on write
              (allowlist of p/br/ul/ol/li/strong/em plus https/mailto anchors)
              and migration 000009 converted all pre-existing rows. */}
          <div
            className="position-detail__description"
            dangerouslySetInnerHTML={{ __html: position.description }}
          />
          {position.videos.map((video, index) => (
            <VideoEmbed
              key={video.id}
              embedUrl={video.embed_url}
              title={video.label ?? `${position.title} video ${index + 1}`}
            />
          ))}
          {error !== null && <p className="form-row__error">{error}</p>}
          <div className="modal__footer">
            {canManage && (
              <>
                <button type="button" className="btn btn--ghost" onClick={onEdit}>
                  Edit
                </button>
                <button type="button" className="btn btn--ghost" onClick={onToggleStatus}>
                  {closed ? "Reopen" : "Close position"}
                </button>
                <button type="button" className="btn btn--danger" onClick={onDelete}>
                  Delete
                </button>
              </>
            )}
            <button type="button" className="btn btn--primary" onClick={onRespond} disabled={closed}>
              Respond
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
